import {
    CalculatedPricelist,
    PeriodImpact,
    Pricelist,
    PricelistDto,
    PricelistRateRow,
    RepricedStarts,
} from 'types/pricelists';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const APIPricelistsSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Pricelists', 'PricelistsCount', 'CalculatedPricelists', 'HourlyRates'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getPricelistById: builder.query<Pricelist, number>({
                query: (entityId) => `/v1/pricelists/${entityId}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => [{ type: 'Pricelists', id: arg }],
            }),
            getPricelistsList: builder.query<Pricelist[], void>({
                query: () => '/v1/pricelists',
                keepUnusedDataFor: 0,
                providesTags: ['Pricelists'],
            }),
            getPricelistsWithFilters: accumulatedQueryWithFilters<CalculatedPricelist>(builder, '/v1/pricelists', {
                providesTags: ['CalculatedPricelists'],
            }),
            getPricelistsCount: injectCountEndPoint(builder, '/v1/pricelists', 'PricelistsCount'),
            findPricelistsByClient: builder.query<CalculatedPricelist[], string>({
                query: (clientId) => `/v1/pricelists/calculated/${clientId}`,
                keepUnusedDataFor: 0,
                providesTags: ['CalculatedPricelists'],
            }),
            findPricelistRates: builder.query<PricelistRateRow[], string>({
                query: (entityId) => `/v1/pricelists/${entityId}/rates`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => [{ type: 'HourlyRates', id: arg }],
            }),
            // What a period starting on a past date re-prices: the contracts and cases that follow the list, and
            // their uninvoiced hours from that date.
            getPricelistPeriodImpact: builder.query<PeriodImpact, { id: number } & RepricedStarts>({
                query: ({ id, validFrom, fromStart }) => {
                    const params = new URLSearchParams();
                    validFrom.forEach((date) => params.append('validFrom', date));
                    if (fromStart) params.set('fromStart', 'true');
                    return `/v1/pricelists/${id}/period-impact?${params.toString()}`;
                },
                keepUnusedDataFor: 0,
            }),
            addPricelist: builder.mutation<Pricelist, Partial<PricelistDto>>({
                query: ({ ...body }) => ({
                    url: `/v1/pricelists`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Pricelists', 'CalculatedPricelists', 'HourlyRates'],
            }),
            updatePricelist: builder.mutation<Pricelist, Partial<PricelistDto> & { entityId: string }>({
                query: ({ entityId, ...body }) => ({
                    url: `/v1/pricelists/${entityId}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.entityId
                        ? [
                              'Pricelists',
                              'CalculatedPricelists',
                              'HourlyRates',
                              { type: 'Pricelists', id: arg.entityId },
                              { type: 'HourlyRates', id: arg.entityId },
                          ]
                        : ['Pricelists', 'CalculatedPricelists', 'HourlyRates'],
            }),
            deletePricelist: builder.mutation<boolean, { entityId: string }>({
                query: ({ entityId }) => ({
                    url: `/v1/pricelists/${entityId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.entityId
                        ? [
                              'Pricelists',
                              'CalculatedPricelists',
                              'HourlyRates',
                              { type: 'CalculatedPricelists', id: arg.entityId },
                              { type: 'HourlyRates', id: arg.entityId },
                          ]
                        : ['Pricelists', 'CalculatedPricelists', 'HourlyRates'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetPricelistByIdQuery,
    useFindPricelistsByClientQuery,
    useFindPricelistRatesQuery,
    useLazyFindPricelistRatesQuery,
    useLazyGetPricelistByIdQuery,
    useGetPricelistPeriodImpactQuery,
    useLazyGetPricelistPeriodImpactQuery,
    useLazyGetPricelistsWithFiltersQuery,
    useLazyGetPricelistsCountQuery,
    useAddPricelistMutation,
    useUpdatePricelistMutation,
    useDeletePricelistMutation,
} = APIPricelistsSlice;
